<script setup>
import { computed, nextTick, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import TalkviaFunnelsAPI from 'dashboard/api/talkviaFunnels';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import {
  HTTP_BASE_VARIABLES,
  HTTP_BODY_METHODS,
  HTTP_MAX_TIMEOUT,
  HTTP_METHODS,
  HTTP_MIN_TIMEOUT,
  clampHttpTimeout,
  flattenJsonEntries,
  insertAtCursor,
  sanitizeSaveAs,
  truncateHttpSample,
  variableToken,
} from '../httpRequestNode';

const props = defineProps({
  action: { type: Object, required: true },
  funnelId: { type: [Number, String], default: null },
  upstreamVariables: { type: Array, default: () => [] },
  samples: { type: Object, default: () => ({}) },
  canTest: { type: Boolean, default: false },
});

const emit = defineEmits(['update']);

// Interface texts kept in script, following the funnel page pattern.
const copy = {
  storageWarning:
    'Cabeçalhos e corpo (inclusive tokens de acesso) ficam armazenados na configuração deste fluxo. Somente endereços públicos http/https são aceitos; redes internas são bloqueadas.',
  method: 'Método',
  headers: 'Cabeçalhos',
  noHeaders:
    'Nenhum cabeçalho. Content-Type JSON é enviado automaticamente quando há corpo.',
  variablesHint: 'Variáveis — clique para inserir na posição do cursor',
  branchHint:
    'Respostas 2xx seguem pelas ligações “Sucesso”; erros, bloqueios e tempo esgotado seguem por “Erro”. A resposta (até 16 KB) fica disponível para os próximos nós e para o nó Condição.',
  truncated: 'Resposta truncada em 16 KB',
  sampleHint:
    'A amostra fica salva no fluxo e alimenta as sugestões dos próximos nós.',
  ms: 'ms',
  sampleSavedPrefix: 'Amostra salva em',
};

const rootEl = ref(null);
const cursor = ref({ field: 'body', start: null, end: null });
const testConversationId = ref('');
const isTesting = ref(false);
const testResult = ref(null);

const methodOptions = HTTP_METHODS.map(method => ({
  value: method,
  label: method,
}));

const headers = computed(() =>
  Array.isArray(props.action.httpHeaders) ? props.action.httpHeaders : []
);
const acceptsBody = computed(() =>
  HTTP_BODY_METHODS.includes(props.action.httpMethod)
);
const saveAsName = computed(() => sanitizeSaveAs(props.action.saveAs));
const variables = computed(() => [
  ...HTTP_BASE_VARIABLES,
  ...props.upstreamVariables,
]);
const variableGroups = computed(() => {
  const groups = new Map();
  variables.value.forEach(variable => {
    if (!groups.has(variable.group)) groups.set(variable.group, []);
    groups.get(variable.group).push(variable);
  });
  return Array.from(groups, ([label, items]) => ({ label, items }));
});

const resultEntries = computed(() =>
  testResult.value?.body && typeof testResult.value.body === 'object'
    ? flattenJsonEntries(testResult.value.body)
    : []
);
const resultPreview = computed(() => {
  const body = testResult.value?.body;
  if (body === null || body === undefined) return '';
  return typeof body === 'string' ? body : JSON.stringify(body, null, 2);
});
const savedSampleLabel = computed(() => {
  const sample = props.action.lastTestSample;
  if (!sample?.savedAt) return '';
  return new Date(sample.savedAt).toLocaleString('pt-BR');
});

const update = patch => emit('update', patch);

const updateHeader = (index, key, value) => {
  const next = headers.value.map((header, position) =>
    position === index ? { ...header, [key]: value } : header
  );
  update({ httpHeaders: next });
};

const addHeader = () =>
  update({ httpHeaders: [...headers.value, { key: '', value: '' }] });

const removeHeader = index =>
  update({
    httpHeaders: headers.value.filter((_, position) => position !== index),
  });

// Guarda o campo e a seleção atuais para inserir a variável no cursor.
const trackCursor = event => {
  const target = event.target;
  const holder = target?.closest?.('[data-variable-target]');
  if (!holder || typeof target.selectionStart !== 'number') return;
  cursor.value = {
    field: holder.dataset.variableTarget,
    start: target.selectionStart,
    end: target.selectionEnd,
  };
};

const fieldValue = field => {
  if (field === 'url') return props.action.httpUrl;
  if (field?.startsWith('header-')) {
    return headers.value[Number(field.slice(7))]?.value || '';
  }
  return props.action.httpBody;
};

const applyFieldValue = (field, value) => {
  if (field === 'url') update({ httpUrl: value });
  else if (field?.startsWith('header-'))
    updateHeader(Number(field.slice(7)), 'value', value);
  else update({ httpBody: value });
};

const insertVariable = async token => {
  const field =
    cursor.value.field === 'body' && !acceptsBody.value
      ? 'url'
      : cursor.value.field;
  const { value, cursor: position } = insertAtCursor(
    fieldValue(field),
    variableToken(token),
    cursor.value.start,
    cursor.value.end
  );
  applyFieldValue(field, value);
  cursor.value = { field, start: position, end: position };
  await nextTick();
  const element = rootEl.value?.querySelector(
    `[data-variable-target="${field}"] input, [data-variable-target="${field}"] textarea`
  );
  if (element) {
    element.focus();
    element.setSelectionRange?.(position, position);
  }
};

const copyVariable = async path => {
  try {
    await copyTextToClipboard(
      variableToken(`http.${saveAsName.value}.${path}`)
    );
    useAlert('Variável copiada.');
  } catch {
    useAlert('Não foi possível copiar a variável.');
  }
};

const runTest = async () => {
  if (!props.funnelId || isTesting.value) return;
  isTesting.value = true;
  try {
    const { data } = await TalkviaFunnelsAPI.testHttpRequest(props.funnelId, {
      request: {
        httpUrl: props.action.httpUrl,
        httpMethod: props.action.httpMethod,
        httpHeaders: headers.value.filter(header => header.key),
        httpBody: acceptsBody.value ? props.action.httpBody : '',
        httpTimeout: clampHttpTimeout(props.action.httpTimeout),
      },
      samples: props.samples,
      conversationDisplayId: String(testConversationId.value || '').trim(),
    });
    testResult.value = data;
    if (data.error) useAlert(data.error);
  } catch (error) {
    testResult.value = null;
    useAlert(
      error?.response?.status === 404
        ? 'Conversa não encontrada nesta conta.'
        : 'Não foi possível testar a requisição. Tente novamente.'
    );
  } finally {
    isTesting.value = false;
  }
};

const saveSample = () => {
  if (!testResult.value || testResult.value.error) return;
  const body = truncateHttpSample(testResult.value.body);
  if (body === null) {
    useAlert('A resposta é grande demais para ser salva como amostra.');
    return;
  }
  update({
    lastTestSample: {
      status: testResult.value.status,
      ok: testResult.value.ok,
      body,
      savedAt: new Date().toISOString(),
    },
  });
  useAlert(
    'Amostra salva no nó. Os caminhos ficam disponíveis nos próximos nós.'
  );
};

defineExpose({ runTest, insertVariable, testResult });
</script>

<template>
  <section
    ref="rootEl"
    class="grid gap-3 rounded-lg border border-n-weak bg-n-solid-1 p-3"
    data-testid="funnel-http-request-fields"
    @focusin="trackCursor"
    @keyup="trackCursor"
    @mouseup="trackCursor"
    @select="trackCursor"
  >
    <p
      class="mb-0 flex items-start gap-2 rounded-lg bg-n-amber-2 p-3 text-xs leading-5 text-n-amber-12"
    >
      <span class="i-hugeicons-square-lock-02 size-4 shrink-0" />
      {{ copy.storageWarning }}
    </p>

    <div class="grid gap-3 sm:grid-cols-[8rem_1fr]">
      <div class="grid content-start gap-1">
        <span class="mb-0.5 text-sm font-medium text-n-slate-12">{{
          copy.method
        }}</span>
        <ComboBox
          :model-value="action.httpMethod"
          :options="methodOptions"
          :display-label="action.httpMethod"
          placeholder="Método"
          search-placeholder="Buscar..."
          empty-state="Nenhum resultado encontrado."
          teleport-to-body
          @update:model-value="value => value && update({ httpMethod: value })"
        />
      </div>
      <div data-variable-target="url">
        <Input
          :model-value="action.httpUrl"
          label="URL"
          placeholder="https://api.exemplo.com/clientes?telefone={{contact.phone}}"
          @update:model-value="value => update({ httpUrl: value })"
        />
      </div>
    </div>

    <div class="grid gap-2">
      <div class="flex items-center justify-between gap-2">
        <span class="text-sm font-medium text-n-slate-12">{{
          copy.headers
        }}</span>
        <NextButton
          type="button"
          size="sm"
          variant="ghost"
          color="slate"
          icon="i-hugeicons-add-01"
          label="Adicionar cabeçalho"
          @click="addHeader"
        />
      </div>
      <p v-if="!headers.length" class="mb-0 text-xs text-n-slate-10">
        {{ copy.noHeaders }}
      </p>
      <div
        v-for="(header, index) in headers"
        :key="index"
        class="grid items-end gap-2 sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)_auto]"
        data-testid="funnel-http-header-row"
      >
        <Input
          :model-value="header.key"
          :label="index === 0 ? 'Nome' : ''"
          placeholder="Authorization"
          @update:model-value="value => updateHeader(index, 'key', value)"
        />
        <div :data-variable-target="`header-${index}`">
          <Input
            :model-value="header.value"
            :label="index === 0 ? 'Valor' : ''"
            placeholder="Bearer ..."
            @update:model-value="value => updateHeader(index, 'value', value)"
          />
        </div>
        <NextButton
          type="button"
          size="sm"
          variant="ghost"
          color="ruby"
          icon="i-hugeicons-delete-02"
          :aria-label="`Remover cabeçalho ${header.key || index + 1}`"
          @click="removeHeader(index)"
        />
      </div>
    </div>

    <div v-if="acceptsBody" data-variable-target="body">
      <TextArea
        :model-value="action.httpBody"
        label="Corpo JSON"
        placeholder='{"telefone": "{{contact.phone}}"}'
        :max-length="20000"
        auto-height
        resize
        min-height="7rem"
        max-height="18rem"
        custom-text-area-class="font-mono !text-xs"
        @update:model-value="value => update({ httpBody: value })"
      />
    </div>

    <div class="grid gap-2 rounded-lg border border-dashed border-n-weak p-3">
      <p class="mb-0 text-xs font-medium text-n-slate-11">
        {{ copy.variablesHint }}
      </p>
      <div
        v-for="group in variableGroups"
        :key="group.label"
        class="flex flex-wrap items-center gap-1.5"
      >
        <span class="text-[11px] font-semibold uppercase text-n-slate-10">
          {{ group.label }}
        </span>
        <button
          v-for="variable in group.items"
          :key="variable.token"
          type="button"
          class="rounded-md border border-n-weak px-2 py-0.5 font-mono text-[11px] text-n-slate-11 hover:border-n-brand hover:text-n-brand focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
          :title="variable.label"
          @mousedown.prevent
          @click="insertVariable(variable.token)"
        >
          {{ variableToken(variable.token) }}
        </button>
      </div>
    </div>

    <div class="grid gap-3 sm:grid-cols-2">
      <Input
        :model-value="action.httpTimeout"
        type="number"
        :min="HTTP_MIN_TIMEOUT"
        :max="HTTP_MAX_TIMEOUT"
        label="Tempo limite (segundos)"
        :message="`Entre ${HTTP_MIN_TIMEOUT} e ${HTTP_MAX_TIMEOUT} segundos.`"
        @update:model-value="
          value => update({ httpTimeout: clampHttpTimeout(value) })
        "
      />
      <Input
        :model-value="action.saveAs"
        label="Nome do resultado"
        placeholder="http"
        :message="`Use {{http.${saveAsName}.campo}} nos próximos nós.`"
        @update:model-value="value => update({ saveAs: value })"
        @blur="update({ saveAs: saveAsName })"
      />
    </div>

    <p class="mb-0 flex items-start gap-2 text-xs leading-5 text-n-slate-11">
      <span class="i-hugeicons-information-circle size-4 shrink-0" />
      {{ copy.branchHint }}
    </p>

    <section
      v-if="canTest"
      class="grid gap-3 rounded-lg border border-n-weak bg-n-alpha-1 p-3"
      data-testid="funnel-http-test"
    >
      <div class="grid items-end gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
        <Input
          v-model="testConversationId"
          label="Conversa para as variáveis (opcional)"
          placeholder="Nº da conversa, ex.: 1234"
          inputmode="numeric"
          message="Sem conversa, as variáveis usam dados de exemplo."
        />
        <NextButton
          type="button"
          size="sm"
          icon="i-hugeicons-test-tube"
          label="Testar request"
          :is-loading="isTesting"
          :disabled="isTesting || !action.httpUrl"
          @click="runTest"
        />
      </div>

      <div
        v-if="testResult"
        class="grid gap-2"
        aria-live="polite"
        data-testid="funnel-http-test-result"
      >
        <div class="flex flex-wrap items-center gap-2 text-xs">
          <span
            class="inline-flex items-center gap-1 rounded-md px-2 py-0.5 font-medium"
            :class="
              testResult.ok
                ? 'bg-n-teal-3 text-n-teal-11'
                : 'bg-n-ruby-3 text-n-ruby-11'
            "
          >
            <span
              class="size-3.5"
              :class="
                testResult.ok
                  ? 'i-hugeicons-checkmark-circle-02'
                  : 'i-hugeicons-alert-circle'
              "
            />
            {{
              testResult.status ? `HTTP ${testResult.status}` : 'Sem resposta'
            }}
          </span>
          <span class="inline-flex items-center gap-1 text-n-slate-11">
            <span class="i-hugeicons-timer-02 size-3.5" />
            {{ testResult.duration_ms }} {{ copy.ms }}
          </span>
          <span v-if="testResult.truncated" class="text-n-amber-11">
            {{ copy.truncated }}
          </span>
        </div>
        <p
          v-if="testResult.error"
          class="mb-0 rounded-lg bg-n-ruby-3 px-3 py-2 text-xs font-medium text-n-ruby-11"
        >
          {{ testResult.error }}
        </p>
        <ul
          v-if="resultEntries.length"
          class="mb-0 grid max-h-64 list-none gap-0.5 overflow-auto rounded-lg border border-n-weak bg-n-solid-1 p-2 ps-2"
          data-testid="funnel-http-json-paths"
        >
          <li
            v-for="entry in resultEntries"
            :key="entry.path"
            class="flex min-w-0 items-center gap-2 rounded px-1 py-0.5 text-xs hover:bg-n-alpha-2"
            :style="{ paddingInlineStart: `${0.25 + entry.depth * 0.75}rem` }"
          >
            <span class="min-w-0 truncate font-mono text-n-slate-12">
              {{ entry.path }}
            </span>
            <span class="min-w-0 flex-1 truncate text-n-slate-10">
              {{ entry.preview }}
            </span>
            <NextButton
              type="button"
              size="xs"
              variant="ghost"
              color="slate"
              icon="i-hugeicons-copy-01"
              :aria-label="`Copiar variável ${entry.path}`"
              :title="`Copiar {{http.${saveAsName}.${entry.path}}}`"
              @click="copyVariable(entry.path)"
            />
          </li>
        </ul>
        <pre
          v-else-if="resultPreview"
          class="mb-0 max-h-64 overflow-auto whitespace-pre-wrap break-all rounded-lg border border-n-weak bg-n-solid-1 p-2 font-mono text-xs text-n-slate-12"
          tabindex="0"
          >{{ resultPreview }}</pre
        >
        <div class="flex flex-wrap items-center gap-2">
          <NextButton
            type="button"
            size="sm"
            variant="faded"
            color="slate"
            icon="i-hugeicons-database-02"
            label="Salvar amostra no nó"
            :disabled="Boolean(testResult.error)"
            @click="saveSample"
          />
          <span class="text-[11px] text-n-slate-10">
            {{ copy.sampleHint }}
          </span>
        </div>
      </div>
      <p v-else-if="savedSampleLabel" class="mb-0 text-xs text-n-slate-10">
        {{ copy.sampleSavedPrefix }} {{ savedSampleLabel }}.
      </p>
    </section>
  </section>
</template>
